import { formatEquation, type AlgebraOperation } from '../../shared/algebra.ts';
import type { LessonStage, StageProgress } from '../../shared/types.ts';
import { speakMath } from '../../shared/tutorGuidance.ts';
import { storyFor } from './storyPresentation.ts';
export type PracticePanel = 'model' | 'solve' | 'check';
export type BoardStyle = 'illustration' | 'written';
export function demonstrationProgress(
  step: { id: string; startMs: number; endMs: number } | undefined,
  position: number,
  dividing: boolean,
  preparingAudio: boolean,
  _reducedMotion: boolean,
) {
  // Motion preferences change rendering, never the narrated mathematical state.
  if (!step && !preparingAudio) return 1;
  if (!step) return 0;
  const fraction = Math.max(
    0,
    Math.min(
      1,
      (position - step.startMs) / Math.max(1, step.endMs - step.startMs),
    ),
  );
  if (step.id === 'result') return 1;
  if (step.id === 'factor') return 0.5 + fraction * 0.29;
  if (step.id === 'operation')
    return 0.18 + fraction * (dividing ? 0.31 : 0.61);
  return 0;
}
export function isolated(p: StageProgress) {
  return (
    p.equation.left.x === '1' &&
    p.equation.left.constant === '0' &&
    p.equation.right.x === '0'
  );
}
export function historyLabel(op: AlgebraOperation) {
  return `After ${{ add: 'adding', subtract: 'subtracting', divide: 'dividing by', multiply: 'multiplying by' }[op.kind]} ${op.value} on both sides`;
}
export function viewNarration(
  stage: LessonStage,
  p: StageProgress,
  panel: PracticePanel,
  style: BoardStyle,
) {
  const representation =
    style === 'written' ? 'written steps' : 'tiles and illustration';
  if (panel === 'model')
    return `${stage.prompt} We are building a model using ${representation}. First decide what x means. Then connect the ${storyFor(stage).initialLabel}, the ${storyFor(stage).rateLabel}, and the ${storyFor(stage).totalLabel}. Your draft stays yours; I can demonstrate if you ask.`;
  if (panel === 'check')
    return `Let's check your idea in the original relationship: ${speakMath(formatEquation(stage.initialEquation))}. Your earlier work is on the working pad. Replace x with your value and compare both sides. Equal results show that the value works. Then explain why your method preserved equality.`;
  return `We are using ${representation}. Your current equation is ${speakMath(formatEquation(p.equation))}. ${isolated(p) ? 'You have isolated x. Next, check this value in the original equation; solving and checking are different steps.' : p.history.length ? 'Your previous move is saved. Now choose your next change from this equation. Explain why it keeps both sides equal.' : 'This is your practice turn. Choose a change to both sides, or ask me to demonstrate first. Our aim is to isolate x while preserving the same solutions.'}`;
}
